import { BorderBox } from "@/components/atoms/BorderBox";
import { DynamicHeightScrollArea } from "@/components/atoms/DynamicHeightScrollArea";
import { HoverPopover } from "@/components/atoms/HoverPopover";
import { MarkSearchedPhrase } from "@/components/atoms/MarkSearchedPhrase";
import { SearchInput } from "@/components/molecules/SearchInput";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tables } from "@/lib/supabase/supabase_types_override";
import { Loader, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { SurveyCrmListDialogProps } from ".";
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";
import ErrorPopup from "@/components/custom/InfoPopups/ErrorPopup";
import CreateCrmConfigModal from "@/features/crm_config/components/organisms/CreateCrmConfigModal";
import EditCrmConfigModal from "@/features/crm_config/components/organisms/EditCrmConfigModal";
import { useDeleteCrmConfig } from "@/features/crm_config/hooks/useMutation/useDeleteCrmConfig";
import { DialogDescription } from "@radix-ui/react-dialog";
import { TooltipButton } from "@/components/atoms/TooltipButton";
import { useCrmConfigsByCrmProjectId } from "@/features/crm_config/hooks/useQuery/useCrmConfigsByCrmProjectId";

export function SurveyCrmListDialog({ project, ...props }: SurveyCrmListDialogProps) {
  const projectsCrmConfigs = useCrmConfigsByCrmProjectId(project?.crm_project_id ?? undefined);
  
  const [searchTerm, setSearchTerm] = useState("")
  const filteredCrmConfigs = projectsCrmConfigs.data?.filter(crmConfig => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    if (crmConfig.name?.toLowerCase().includes(searchLower)) return true;
    if (crmConfig.description?.toLowerCase().includes(searchLower)) return true;

    return false;
  }) ?? [];
  // - Delete Confirmation Dialog Logic -
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedSurveyCrmId, setSelectedSurveyCrmId] = useState<string>();
  const onOpenDeleteDialog = (surveyCrmId: string) => {
    setSelectedSurveyCrmId(surveyCrmId);
    setShowDeleteDialog(true);
  };
  const onCloseDeleteDialog = () => {
    setShowDeleteDialog(false);
    setSelectedSurveyCrmId(undefined);
  }; 
  const { mutateAsync: deleteCrmConfig, isPending: isDeletingCrmConfig } = useDeleteCrmConfig();
  const deleteCrmConfigHandler = (crmConfigId: Tables<'crm_config'>['id'] | undefined) => {
    if (!crmConfigId) return;
    deleteCrmConfig(crmConfigId)
      .then(() => {
        SuccessPopup("CRM configuration deleted successfully");
        onCloseDeleteDialog();
      })
      .catch((err) => {
        console.log(err);
        ErrorPopup("Something went wrong");
      });
  };

  const [isAddSurveyCrmDialogOpen, setAddSurveyCrmDialogOpen] = useState(false);
  const openAddSurveyCrmDialog = () => {
    setAddSurveyCrmDialogOpen(true);
  }

  const [isEditSurveyCrmDialogOpen, setEditSurveyCrmDialogOpen] = useState(false);
  const [selectedCrmConfig, setSelectedCrmConfig] = useState<Tables<'crm_config'> | null>(null);
  const openEditSurveyCrmDialog = (data: Tables<'crm_config'>) => {
    setSelectedCrmConfig(data);
    setEditSurveyCrmDialogOpen(true);
  };

  if(!project) { 
    return null;
  }

  return (
    <>
      <Dialog open={showDeleteDialog} onOpenChange={onCloseDeleteDialog}>
        <DialogContent className="">
          <DialogHeader>
            <DialogTitle>Are you sure you want to delete this CRM configuration?</DialogTitle>
            <DialogDescription>
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={onCloseDeleteDialog}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => deleteCrmConfigHandler(selectedSurveyCrmId)}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateCrmConfigModal 
        showModal={isAddSurveyCrmDialogOpen}
        setShowModal={() => setAddSurveyCrmDialogOpen(false)}
        crm={project?.crm}
        crmProject={project?.crm_project}
      />

      <EditCrmConfigModal
        showModal={isEditSurveyCrmDialogOpen}
        setShowModal={() => setEditSurveyCrmDialogOpen(false)}
        crm={project?.crm}
        crmProject={project?.crm_project}
        
        crmConfigData={selectedCrmConfig}
      />

      <Dialog {...props} >
        <DialogContent className="w-full h-screen max-w-none" >
          <DialogHeader>
            <DialogTitle>Survey CRMs</DialogTitle>
          </DialogHeader>
          <Separator />

          <div>
            <SearchInput
              placeholder="Filter"
              value={searchTerm}
              setValue={setSearchTerm}
            />
          </div>

            <div className="grid flex-1 grid-cols-1 gap-2">
              <div className="flex flex-col flex-1">
                <Label>CRM Configurations</Label>
                <BorderBox className="flex flex-col items-stretch flex-1 mt-2 bg-white">
                  {(() => {
                    if (projectsCrmConfigs.isLoading) {
                      return (
                        <div className="flex items-center justify-center w-full">
                          <Loader className="animate-spin" />
                        </div>
                      )
                    }
                    if (projectsCrmConfigs.isError) {
                      return (
                        <div className="flex items-center justify-center w-full">
                          Error loading CRM configurations!
                        </div>
                      )
                    }
                    return (
                      <DynamicHeightScrollArea offset={140}>
                        <div
                          className="flex flex-col gap-2"
                        >
                          {filteredCrmConfigs.map((crmConfig) => (
                            <BorderBox key={crmConfig.id}
                              className={`flex items-center gap-2 border-l-4 min-w-0`}
                              size='sm'
                            >
                              <MarkSearchedPhrase
                                text={crmConfig.name || ''}
                                searchTerm={searchTerm}
                                className="font-medium whitespace-nowrap"
                              />
                              <HoverPopover
                                onHover
                                content={
                                  <MarkSearchedPhrase
                                    text={crmConfig.description || ''}
                                    searchTerm={searchTerm}
                                  />
                                }
                              >
                                <MarkSearchedPhrase
                                  text={crmConfig.description || ''}
                                  searchTerm={searchTerm}
                                  className="flex-1 min-w-0 line-clamp-1 text-muted-foreground"
                                />
                              </HoverPopover>
                              <div className="flex flex-shrink-0 gap-2 ml-auto">

                                <TooltipButton
                                  size='action'
                                  disabled={isDeletingCrmConfig}
                                  onClick={() => openEditSurveyCrmDialog(crmConfig)}
                                  tooltipContent="Edit"
                                >
                                  {isDeletingCrmConfig ? <Loader className="w-4 h-4 animate-spin" /> : <Pencil size={16} />}
                                </TooltipButton>

                                <TooltipButton
                                  size='action'
                                  variant='destructive'
                                  disabled={isDeletingCrmConfig}
                                  onClick={() => onOpenDeleteDialog(crmConfig.id)}
                                  tooltipContent="Delete"
                                >
                                  {isDeletingCrmConfig ? <Loader className="w-4 h-4 animate-spin" /> : <Trash2 size={16} />}
                                </TooltipButton>

                              </div>
                            </BorderBox>
                          ))}
                        </div>
                      </DynamicHeightScrollArea>
                    )
                  })()}
                </BorderBox>
              </div>
            </div>
          <BorderBox variant='info' size='sm'>
              <span>
                If you would like to add additional CRM Configuration, <Button onClick={openAddSurveyCrmDialog} type="button" className="h-auto p-0 m-0 text-xs text-blue-500" variant='link'>click here.</Button>
              </span>
          </BorderBox>

          <DialogFooter className="sm:justify-end">
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Close
              </Button>
            </DialogClose>
          </DialogFooter>

        </DialogContent>
      </Dialog>
    </>
  );
}
